import { Component } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ConsultationsService } from '../../../../core/services/consultation/consultations.service';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { FormsModule } from '@angular/forms';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { StatusUtils } from '../../../../utils/status-utils';
import { environment } from '../../../../../environments/environment';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { ConsultationListModel } from '../../../../core/models/consultation/consultation-list.model';
import { CustomerListModel } from '../../../../core/models/customer/customer-list.model';
import { AIReportStatus } from '../../../../core/models/report/ai-report-status.model';
import { CaseStatus } from '../../../../../enums/case-status.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablesComponent } from '../../../components/tables/tables/tables.component';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { FilterModel } from '../../../../core/models/filters copy/filters.model';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
@Component({
  selector: 'app-consultation-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    TablesComponent,
  ],
  templateUrl: './consultation-list.component.html',
  styleUrl: './consultation-list.component.scss',
  providers: [DatePipe],
})
export class ConsultationListComponent {
  caseStatus = CaseStatus;
  currentUser: UserSessionModel | null = null;
  consultationList: ConsultationListModel[] = [];
  consultationCount: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  totalConsultations: number = 0;
  filterForm = {
    pid: '',
    startCreatedDate: null as string | null,
    endCreatedDate: null as string | null,
    caseId: '',
    nameSurname: '',
    customerCode: null as any,
    createdBy: '',
    status: null as any,
    ai: null as any,
  };
  organizationList: CustomerListModel[] = [];
  caseStatuses = StatusUtils.getFilterCaseStatuses();
  aiReportStatuses: AIReportStatus[] = [
    { code: 'true', title: 'Yes' },
    { code: 'false', title: 'No' },
  ];
  maxDate: string;
  consultationListLoading: boolean = true;
  filters: FilterModel[] = [
    //AGB Code
    {
      type: 'input',
      label: 'agbCode',
      placeholder: 'filterByAgbCode',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'startCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'StartCreatedDateTime',
      value: null,
    },
    //EndCreateDate
    {
      type: 'date',
      label: 'endCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'EndCreatedDateTime',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //Status
    {
      type: 'select',
      label: 'status',
      placeholder: 'selectStatus',
      filterId: 'Status',
      value: null,
    },
  ];
  sort: SortModel | null = null;
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-125',
    'w-200',
    'w-250',
    'w-175',
    'w-175',
    'w-250',
    'w-200',
  ];

  constructor(
    private consultationsService: ConsultationsService,
    private currentUserService: CurrentUserService,
    private customersService: CustomersService,
    private signalRService: SignalRService,
    private translate: TranslateService
  ) {
    this.translate.setDefaultLang('tr');
    this.translate.use('tr');
    this.createConsultationListSignalRConnection();
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.columns = [
      { key: 'pidNo', translateKey: 'agbCode' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'patientName', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      { key: 'createdBy', translateKey: 'createdBy' },
      { key: 'patientStatus', translateKey: 'status' },
      { key: 'AI', translateKey: 'AI' },
    ];

    setTimeout(() => {
      this.tableHeader = this.translate.instant('consultationList');
      this.footerText = this.translate.instant('footerText.consultation');
    }, 100);
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.getConsultationList(this.currentPage, this.pageSize);
    this.getOrganizations();
  }

  createConsultationListSignalRConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = signalRBasePath + '/patient-hub';
    this.signalRService.connect(hubUrl);
    this.signalRService.on('receivePatientRecordCaseCreated', (message) => {
      const parsedConsultation = JSON.parse(message);
      if (parsedConsultation) {
        const consultation = {
          patientName: parsedConsultation.PatientName,
          patientLastName: parsedConsultation.PatientLastName,
          patientRecordCaseId: parsedConsultation.PatientRecordCaseId,
          customerCode: parsedConsultation.CustomerCode,
          createdBy: parsedConsultation.CreatedBy,
          patientStatus: parsedConsultation.PatientStatus,
          pidNo: parsedConsultation.PIDNo,
          createdDate: parsedConsultation.CreatedDate,
          customerName: '',
          analysisStartTime: '',
          analysisEndTime: '',
          analysisResult: '',
          consultReport: '',
          patientRecordCaseNumber: null as number | any,
        };
        this.consultationCount++;
        this.consultationList = [consultation, ...this.consultationList];
      }
    });
  }

  filterConsultationList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getConsultationList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getConsultationList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getConsultationList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  filterPatientList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getConsultationList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getConsultationList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getConsultationList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getConsultationList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.consultationListLoading = true;
    this.consultationsService
      .consultationList(page, size, filters, sort)
      .subscribe({
        next: (res) => {
          this.consultationList = res.items;
          this.consultationCount = res.count;
          this.totalPage = res.pages;
          this.pageSize = res.size;
          this.currentPage = page;
          this.consultationListLoading = false;
        },
      });
  }

  clearFilters() {
    this.filterForm.pid = '';
    this.filterForm.startCreatedDate = null;
    this.filterForm.endCreatedDate = null;
    this.filterForm.caseId = '';
    this.filterForm.nameSurname = '';
    this.filterForm.customerCode = null;
    this.filterForm.createdBy = '';
    this.filterForm.status = null;
    this.filterForm.ai = null;

    this.getConsultationList(this.currentPage, this.pageSize);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getConsultationList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getConsultationList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'Patient.PIDNo';
      case 'patientName':
        return 'Patient.NameSurname';
      case 'patientStatus':
        return 'PatientRecordCases.Status';
      case 'createdBy':
        return 'CreatedBy';
      default:
        return column;
    }
  }

  getStatusClass(status: number): string {
    return StatusUtils.getCaseStatusClass(status);
  }

  getStatusName(status: number): string {
    return StatusUtils.getCaseStatusText(status);
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }
}
